/**
 * The walk: the Decide beat one decision at a time, desk by desk (DESIGN §6). It sequences the page's own controls over the same draft; DecidePage supplies the
 * control, the current-setting line, the desk notes, the rationale, the cards and the thesis as render props. A decision step ends in Maintain or Next.
 */
import { Fragment, useEffect, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import type { ContentCatalog, DecisionCatalogEntry, DraftView } from "@bsim/contracts";
import type { DecisionFamily, DecisionId } from "@bsim/domain";
import { GxDescribed } from "../../theme/GxTerm";
import { deskLabel } from "../labels";
import { decisionReviewState } from "../reviewState";
import { REVIEW_TAG, REVIEW_WORDS } from "../reviewWords";
import { GxMenu } from "../../theme/Menu";
import { stepAfter, stepKey, type WalkStep } from "./walkSteps";
import "./walk.css";

export interface WalkProps {
  readonly draft: DraftView;
  readonly catalog: ContentCatalog;
  readonly steps: readonly WalkStep[];
  readonly current: WalkStep;
  /** null = past the last step: go to Review & sign. */
  readonly onStep: (s: WalkStep | null) => void;
  /** Round editable and no teammate lock on the desk. */
  readonly canMark: boolean;
  readonly onMaintain: (id: DecisionId) => void;
  readonly controlFor: (decisionId: string) => ReactNode;
  readonly sentence: (decisionId: string) => ReactNode;
  readonly deskNotes: (desk: DecisionFamily) => ReactNode;
  /** `asked`: the player pressed Next while this reason is still owed; the box says what is missing, once. */
  readonly rationale: (desk: DecisionFamily, asked: boolean) => ReactNode;
  /** The page's bar carries the one primary: it calls what Next does here, through this ref. */
  readonly advanceRef?: MutableRefObject<(() => void) | null> | undefined;
  readonly cards: ReactNode;
  readonly thesis: ReactNode;
  readonly box: (entry: DecisionCatalogEntry) => ReactNode;
}

function Heading({ children, testId }: { readonly children: ReactNode; readonly testId: string }) {
  return <h2 className="gx-h2 gx-walk-title" tabIndex={-1} data-testid={testId}>{children}</h2>;
}

export function Walk(p: WalkProps) {
  const { draft, catalog, steps, current, onStep } = p;
  const next = stepAfter(steps, current);
  // A reason the round requires and the player left blank is asked for here, where it is written: Next never carries on past it only to come back to it at the end.
  const owedHere = current.kind === "rationale" && draft.missingRationales.includes(current.desk);
  const [asked, setAsked] = useState<string | null>(null);
  const advance = () => {
    if (owedHere) { setAsked(stepKey(current)); body.current?.querySelector<HTMLElement>("textarea")?.focus(); return; }
    onStep(next);
  };
  if (p.advanceRef !== undefined) p.advanceRef.current = advance;
  // After the first step, a move puts the reader at the new step's title.
  const body = useRef<HTMLDivElement>(null);
  const seen = useRef<string | null>(null);
  const key = stepKey(current);
  useEffect(() => {
    if (seen.current !== null && seen.current !== key) {
      const title = body.current?.querySelector<HTMLElement>("[data-testid='walk.title']");
      // The title takes focus where it is: the page does not jump to it, so the page's own title stays in view; the window moves only when the new step's title is off the screen.
      title?.focus({ preventScroll: true });
      const top = title?.getBoundingClientRect().top;
      if (top !== undefined && (top < 0 || top > window.innerHeight - 160)) title?.scrollIntoView?.({ block: "center" });
    }
    seen.current = key;
  }, [key]);

  let content: ReactNode;
  if (current.kind === "decision") {
    const entry = catalog.decisions.find((d) => d.decisionId === current.decisionId);
    const state = decisionReviewState(draft, current.decisionId);
    const card = entry === undefined ? undefined : catalog.conceptCards.find((c) => c.id === entry.coachCardIds[0]);
    const maintainable = p.canMark && state === "toReview";
    const first = current.index === 1;
    const here = steps.filter((x): x is Extract<WalkStep, { kind: "decision" }> => x.kind === "decision" && x.desk === current.desk);
    content = entry === undefined ? (
      <div className="gx-walk-acts"><button type="button" className="gx-btn gx-btn--secondary" data-testid="walk.next" onClick={advance}>Next</button></div>
    ) : (
      <>
        <div className="gx-walk-head">
          <p className="gx-eyebrow" data-testid="walk.position">{deskLabel(catalog, current.desk)} · decision {current.index} of {current.of}</p>
          {here.length < 2 ? null : (
            <GxMenu label="Jump to a decision" testId="walk.jump" align="right" className="gx-walk-jump" trigger={<><span className="gx-walk-jump-l">Jump to a decision</span><span className="gx-walk-jump-s" aria-hidden="true">Jump</span> <span aria-hidden="true">{"\u25BE"}</span></>}
              items={here.map((h) => ({ key: h.decisionId, label: catalog.decisions.find((d) => d.decisionId === h.decisionId)?.label ?? h.decisionId, hint: REVIEW_TAG[decisionReviewState(draft, h.decisionId)], onSelect: () => onStep(h) }))} />
          )}
        </div>
        {first ? p.deskNotes(current.desk) : null}
        <Heading testId="walk.title">
          <GxDescribed label={entry.label} plain={entry.help} {...(card === undefined ? {} : { conceptCardId: card.id })}>{entry.label}</GxDescribed>
        </Heading>
        <p className="gx-walk-help" data-testid="walk.help">{entry.help}</p>
        <div className="gx-walk-now" data-testid="walk.sentence">{p.sentence(entry.decisionId)}</div>
        <div className="gx-walk-control">{p.controlFor(entry.decisionId)}</div>
        <Fragment key={entry.decisionId}>{p.box(entry)}</Fragment>
        <div className="gx-walk-acts">
          {maintainable ? <button type="button" className="gx-btn gx-btn--secondary" data-testid="walk.maintain" onClick={() => { p.onMaintain(current.decisionId); onStep(next); }}>{REVIEW_WORDS.maintain}</button> : null}
          <button type="button" className="gx-btn gx-btn--secondary" data-testid="walk.next" onClick={advance}>Next</button>
        </div>
      </>
    );
  } else {
    const heading = current.kind === "rationale" ? `${deskLabel(catalog, current.desk)} rationale` : current.kind === "cards" ? "Events" : "Your thesis for the year";
    const testId = current.kind === "rationale" ? "walk.rationale" : current.kind === "cards" ? "walk.cards" : "walk.thesis";
    content = (
      <div data-testid={testId}>
        <Heading testId="walk.title">{heading}</Heading>
        {current.kind === "rationale" ? p.rationale(current.desk, owedHere && asked === key) : current.kind === "cards" ? p.cards : p.thesis}
        {/* The thesis is the last step: the page's bar carries the one action from here (add the line, then review). */}
        {current.kind === "thesis" ? null : (
          <div className="gx-walk-acts">
            <button type="button" className="gx-btn gx-btn--secondary" data-testid="walk.next" onClick={advance}>Next</button>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="gx-walk-grid" data-testid="decide.walk">
      <section className="gx-card gx-walk" data-testid="walk.step" data-step={key} ref={body}>
        <div className="gx-card-b gx-walk-b">{content}</div>
      </section>
    </div>
  );
}
